import { useState, useEffect } from "react";
import { EXPENSE_CATEGORIES } from "../../services/expenseService";
import { getProjects } from "../../services/projectService";
import { useContacts } from "../../contexts/ContactsContext";

const today = new Date().toISOString().slice(0, 10);

const TAX_RATES = [
  { value: 0, label: "0% (No VAT)" },
  { value: 5, label: "5% (Reduced rate)" },
  { value: 20, label: "20% (Standard rate)" },
];


// ─── Shared styles (match InvoicingDashboard pattern) ────────────────────────
const inp = {
  display: "block", width: "100%", marginTop: "0.3rem",
  padding: "0.5rem 0.7rem", borderRadius: 6, border: "1px solid #ddd",
  fontSize: "0.92rem", boxSizing: "border-box", fontFamily: "Raleway, sans-serif",
};
const lbl = { display: "block", fontSize: "0.8rem", fontWeight: 600, color: "#555" };
const btnPrimary = {
  padding: "0.55rem 1.2rem", background: "#E4E69B", border: "none",
  borderRadius: 7, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#0C2D48",
};
const btnGhost = {
  padding: "0.55rem 1.2rem", background: "transparent", border: "1px solid #ddd",
  borderRadius: 7, fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#333",
};
// ─── Component ────────────────────────────────────────────────────────────────
function ExpenseForm({ expense, onSave, onClose }) {
  const isEdit = !!expense;
  const { contacts } = useContacts();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    let alive = true;
    getProjects()
      .then((items) => {
        if (alive) setProjects(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (alive) setProjects([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  // Determine if the stored category is custom
  const storedCat = expense?.category ?? "";
  const isCustom = storedCat && !EXPENSE_CATEGORIES.includes(storedCat);

  const [title, setTitle] = useState(expense?.title ?? "");
  const [amount, setAmount] = useState(expense?.amount ?? "");
  const [date, setDate] = useState(expense?.date ?? today);
  const [category, setCategory] = useState(isCustom ? "Custom" : storedCat);
  const [customCategory, setCustomCategory] = useState(isCustom ? storedCat : "");
  const [projectId, setProjectId] = useState(expense?.project_id ?? null);
  const [contactId, setContactId] = useState(expense?.contact_id ?? null);
  const [isBillable, setIsBillable] = useState(expense?.is_billable ?? false);
  const [isBilled, setIsBilled] = useState(expense?.is_billed ?? false);
  const [taxRate, setTaxRate] = useState(expense?.tax_rate ?? 0);
  const [notes, setNotes] = useState(expense?.notes ?? "");

  const amountNum = parseFloat(amount) || 0;
  const vatAmount = amountNum * taxRate / 100;
  const totalIncVat = amountNum + vatAmount;

  const resolvedCategory = category === "Custom" ? customCategory.trim() : category;
  const canSave = title.trim() && amountNum > 0 && date && resolvedCategory;

  function handleSave() {
    if (!canSave) return;
    onSave({
      id: expense?.id,
      title: title.trim(),
      amount: amountNum,
      date,
      category: resolvedCategory,
      project_id: projectId,
      contact_id: contactId,
      receipt_url: expense?.receipt_url ?? null,
      notes: notes.trim(),
      is_billable: isBillable,
      is_billed: isBillable ? isBilled : false,
      tax_rate: taxRate,
      created_at: expense?.created_at ?? new Date().toISOString(),
    });
  }

  return (
    <div className="modal-overlay">
      <div
        className="modal-content neutral"
        style={{ maxWidth: 620, width: "95vw", maxHeight: "90vh", overflowY: "auto", overflowX: "hidden", boxSizing: "border-box" }}
      >
        <button className="close-button" onClick={onClose}>✕</button>
        <h3 style={{ marginBottom: "1.25rem", fontFamily: "Poppins, sans-serif" }}>
          {isEdit ? "Edit Expense" : "New Expense"}
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

          {/* Title */}
          <label style={lbl}>
            Expense title <span style={{ color: "#E96A6A" }}>*</span>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Google Ads — April"
              style={inp}
              autoFocus
            />
          </label>

          {/* Amount + Date */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <label style={lbl}>
              Amount (£) <span style={{ color: "#E96A6A" }}>*</span>
              <input
                type="number" min="0" step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                style={inp}
              />
            </label>
            <label style={lbl}>
              Date <span style={{ color: "#E96A6A" }}>*</span>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                style={inp}
              />
            </label>
          </div>

          {/* Category */}
          <div>
            <div style={{ ...lbl, marginBottom: "0.3rem" }}>
              Category <span style={{ color: "#E96A6A" }}>*</span>
            </div>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              style={{ ...inp, fontFamily: "Raleway, sans-serif" }}
            >
              <option value="">— Select category —</option>
              {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="Custom">Custom…</option>
            </select>
            {category === "Custom" && (
              <input
                value={customCategory}
                onChange={e => setCustomCategory(e.target.value)}
                placeholder="Enter custom category"
                style={{ ...inp, marginTop: "0.4rem" }}
                autoFocus
              />
            )}
          </div>

          {/* Project */}
          <label style={lbl}>
            Attach to project{" "}
            <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
            <select
              value={projectId != null ? String(projectId) : ""}
              onChange={e => setProjectId(e.target.value ? Number(e.target.value) : null)}
              style={{ ...inp, fontFamily: "Raleway, sans-serif" }}
            >
              <option value="">— No project —</option>
              {projects.map(p => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}{p.status && p.status !== "active" ? ` (${p.status})` : ""}
                </option>
              ))}
            </select>
          </label>

          {/* Supplier / contact */}
          <label style={lbl}>
            Supplier / contact{" "}
            <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
            <select
              value={contactId != null ? String(contactId) : ""}
              onChange={e => setContactId(e.target.value || null)}
              style={{ ...inp, fontFamily: "Raleway, sans-serif" }}
            >
              <option value="">— No supplier —</option>
              {contacts.map(c => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}{c.company ? ` (${c.company})` : ""}
                </option>
              ))}
            </select>
          </label>

          {/* Billable toggles */}
          <div style={{
            padding: "0.85rem 1rem", background: "#f8f9fa",
            border: "1px solid #e5e7eb", borderRadius: 8, boxSizing: "border-box",
          }}>
            <label style={{
              display: "flex", alignItems: "center", gap: "0.6rem",
              cursor: "pointer", fontWeight: 600, fontSize: "0.88rem", color: "#333",
            }}>
              <input
                type="checkbox" checked={isBillable}
                onChange={e => { setIsBillable(e.target.checked); if (!e.target.checked) setIsBilled(false); }}
                style={{ width: 16, height: 16, accentColor: "#0C2D48", flexShrink: 0 }}
              />
              Billable to a client
            </label>
            {isBillable && (
              <label style={{
                display: "flex", alignItems: "center", gap: "0.6rem",
                cursor: "pointer", fontWeight: 500, fontSize: "0.85rem", color: "#555",
                marginTop: "0.5rem", paddingLeft: "1.6rem",
              }}>
                <input
                  type="checkbox" checked={isBilled}
                  onChange={e => setIsBilled(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: "#0C2D48", flexShrink: 0 }}
                />
                Already invoiced to the client
              </label>
            )}
          </div>

          {/* VAT */}
          <div>
            <div style={{ ...lbl, marginBottom: "0.3rem" }}>
              VAT rate{" "}
              <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              <select
                value={taxRate}
                onChange={e => setTaxRate(Number(e.target.value))}
                style={{ ...inp, width: "auto", minWidth: 170, fontFamily: "Raleway, sans-serif" }}
              >
                {TAX_RATES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {taxRate > 0 && amountNum > 0 && (
                <div style={{ fontSize: "0.82rem", color: "#555", lineHeight: 1.7 }}>
                  <span>VAT: <strong>£{vatAmount.toFixed(2)}</strong></span>
                  <span style={{ margin: "0 0.5rem", color: "#ccc" }}>·</span>
                  <span>Total inc. VAT: <strong>£{totalIncVat.toFixed(2)}</strong></span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <label style={lbl}>
            Notes <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Any additional details…"
              style={{ ...inp, resize: "vertical" }}
            />
          </label>

        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem" }}>
          <button onClick={handleSave} style={btnPrimary} disabled={!canSave}>
            {isEdit ? "Save Changes" : "Save expense"}
          </button>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default ExpenseForm;
